<template>
  <div>
    <header class="shadow-b flex h-14 items-center bg-white dark:border-b dark:border-gray-700 dark:bg-gray-800">
      <button @click.prevent="toggleMainMenu"
        class="ring-primary-200 ml-1 inline-flex h-12 w-12 items-center justify-center rounded-lg focus:outline-none focus:ring focus:ring-inset dark:ring-gray-600 lg:hidden"
        :aria-label="__('Toggle Sidebar')" :aria-expanded="mainMenuShown ? 'true' : 'false'">
        <Icon type="menu" />
      </button>

      <div class="hidden h-full w-1/5 flex-shrink-0 items-center md:flex lg:w-60 lg:bg-[#00254f]">
        <Link :href="$url('/')"
          class="ring-primary-200 ml-2 flex h-12 items-center rounded-lg text-gray-900 hover:text-gray-500 focus:outline-none focus:ring focus:ring-inset active:text-gray-900 dark:text-gray-400 dark:ring-gray-600 dark:hover:text-gray-300 dark:active:text-gray-500 lg:ml-0"
          :aria-label="appName">
        <AppLogo :light="true" class="hidden h-6 lg:block" />
        <AppLogo class="h-6 lg:hidden" />
        </Link>

        <LicenseWarning />
      </div>

      <div class="flex flex-1 px-4 sm:px-8 lg:px-12">
        <div class="ml-auto flex items-center pl-6">
          <div class="px-3" v-if="showClock">{{ formattedClockTime }}</div>

          <ThemeDropdown />
          <NotificationCenter v-if="notificationCenterEnabled" />

          <MainMenuDropdown>
            <DropdownTrigger
              class="flex h-10 items-center rounded-lg text-sm font-semibold focus:outline-none focus:ring-[0] px-3 text-gray-600"
              role="navigation"
            >
              <span class="whitespace-nowrap">
                {{ selectedLocaleLabel }}
              </span>
            </DropdownTrigger>

            <template #menu>
              <DropdownMenu width="100" class="px-1">
                <nav class="py-1">
                  <DropdownMenuItem
                    v-for="locale in availableLocales"
                    :key="locale.value"
                    as="button"
                    @click="chooseLocale(locale.value)"
                  >
                    {{ locale.label }}
                  </DropdownMenuItem>
                </nav>
              </DropdownMenu>
            </template>
          </MainMenuDropdown>

          <UserMenu class="ml-2 hidden md:flex" />
        </div>
      </div>
    </header>

    <!-- Mobile Sidebar -->
    <div class="w-60 lg:hidden" :class="{ hidden: !mainMenuShown }">
      <div class="fixed inset-0 z-50 flex">
        <div class="fixed inset-0" aria-hidden="true">
          <div @click="toggleMainMenu" class="absolute inset-0 bg-gray-600 opacity-75 dark:bg-gray-900" />
        </div>

        <div class="max-w-xxs relative flex w-full flex-1 flex-col bg-[#002d5f] dark:bg-gray-800">
          <div class="absolute top-0 right-0 -mr-12 pt-2">
            <button @click.prevent="toggleMainMenu"
              class="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              :aria-label="__('Close Sidebar')">
              <!-- Heroicon name: outline/x -->
              <svg class="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div>
            <Link :href="$url('/')"
              class="flex h-12 items-center rounded-lg px-2 text-gray-900 hover:text-gray-500 focus:outline-none focus:ring focus:ring-inset active:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:active:text-gray-500"
              :aria-label="appName">
            <AppLogo :light="true" class="h-6" />
            </Link>
          </div>

          <div class="overflow-x-auto">
            <MainMenu class="mt-3 px-2" />
          </div>

          <div class="absolute left-0 bottom-0 right-0 bg-[#002d5f] py-1 px-2 dark:bg-gray-800 md:hidden">
            <UserMenu :mobile="true" />
          </div>

          <div class="w-14 flex-shrink-0" aria-hidden="true">
            <!-- Dummy element to force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LicenseWarning from '@/components/LicenseWarning'
import { mapGetters, mapMutations } from 'vuex'

export default {
  methods: {
    ...mapMutations(['toggleMainMenu']),
    screenResizeFn() {
      this.currentScreenWidth = window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth
    },
    startClock() {
      this.setDateTime()

      this.clockIntervalInstance = setInterval(() => this.setDateTime(), 1000)
    },
    destroyClock() {
      clearInterval(this.clockIntervalInstance)
    },
    setDateTime() {
      if (!config?.clockTimezone) {
        this.currentDateTime = new Date()

        return
      }

      this.currentDateTime = new Date(
        new Date().toLocaleString('en', {
          timeZone: config.clockTimezone || '',
        })
      )
    },
    startScreenWidth() {
      this.currentScreenWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth

      this.currentScreenWidthInstance = window.addEventListener('resize', () => this.screenResizeFn())
    },
    destroyScreenWidth() {
      window.removeEventListener('resize', () => this.screenResizeFn())
    },

    async chooseLocale(locale){
      await Nova.request()
        .post(`/${locale}/nova/set-locale`)
        .then(() => window.location.reload())
        .catch((err) => console.error(err))
    }
  },
  components: { LicenseWarning },

  data() {
    return {
      clockIntervalInstance: null,
      currentDateTime: null,
      currentScreenWidth: 0,
      currentScreenWidthInstance: null,
    }
  },

  watch: {
    mainMenuShown(newValue) {
      if (newValue == true) {
        document.body.classList.add('overflow-y-hidden')
        return
      }

      document.body.classList.remove('overflow-y-hidden')
    },
  },

  mounted() {
    this.startClock()
    this.startScreenWidth()
    this.destroyScreenWidth()
  },

  beforeUnmount() {
    this.destroyClock()
    document.body.classList.remove('overflow-y-hidden')
  },

  computed: {
    ...mapGetters(['mainMenuShown']),

    globalSearchEnabled() {
      return Nova.config('globalSearchEnabled')
    },

    notificationCenterEnabled() {
      return Nova.config('notificationCenterEnabled')
    },

    appName() {
      return Nova.config('appName')
    },

    showClock() {
      return !!(config?.clockTimezone && this.currentScreenWidth > 400)
    },

    formattedClockTime() {
      return new Intl.DateTimeFormat('en-GB', {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
      }).format(this.currentDateTime)
    },

    availableLocales(){
      const locales = config.locales
      const localesLength = locales.length

      return locales
      .filter(locale => {
        if(localesLength < 2){
          return true
        }

        return locale !== config.selectedLocale
      })
      .map(locale => {
        return {
          label: locale.toUpperCase(),
          value: locale,
        }
      })
    },
    selectedLocaleLabel(){
      return config.selectedLocale.toUpperCase()
    }
  },
}
</script>
